import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { uid } from 'react-uid';
import type { GetDataError } from 'restful-react';
import styled from 'styled-components';
import type { Location, LocationSummary, ResponseApiErrors } from '../../app/ApiGen';
import type { InputComponentProps, ValueComponentProps } from '../../design-system';
import { getAddressAsString } from '../../utils/locations/address';
import type { Normalised } from '../../utils/request';
import type { LocationDetails } from '../../views/Organisations/Organisation/Create/CreateChargePoint/types';
import { AutocompleteOption } from '../AutocompleteOption';
import { type AutocompleteContents, AutocompleteWrapper, renderAutocompleteInput } from './SimpleAutocomplete';

const StyledLink = styled(Link)`
  text-decoration: none;
`;

type LocationValueProps = ValueComponentProps<LocationDetails | Normalised<LocationSummary> | undefined> & {
  isLink?: boolean;
  organisationSlug?: string;
};

function getLocationAttributes(location: LocationDetails | Normalised<LocationSummary>) {
  return { id: location.id, name: location.name, address: location.address };
}

export const LocationValue: React.FC<LocationValueProps> = ({
  isLink = true,
  organisationSlug,
  value,
}: LocationValueProps) => {
  if (!value) {
    return null;
  }

  const { id, name, address } = getLocationAttributes(value);

  if (isLink && organisationSlug) {
    return (
      <StyledLink to={`/organisations/${organisationSlug}/locations/${id}`}>
        <Typography color="primary" variant="body2">
          {name}
        </Typography>
        <Typography color="textSecondary" variant="body2">
          {getAddressAsString(address)}
        </Typography>
      </StyledLink>
    );
  }

  return (
    <div>
      <Typography color="textPrimary" variant="body2">
        {name}
      </Typography>
      <Typography color="textSecondary" variant="body2">
        {getAddressAsString(address)}
      </Typography>
    </div>
  );
};

type LocationInputProps = InputComponentProps<string> & {
  dataLoading: boolean;
  dataError: GetDataError<ResponseApiErrors> | null;
  options?: Normalised<Location>[];
};

export const LocationInput: React.FC<LocationInputProps> = ({
  dataLoading,
  options,
  value,
  name,
  onBlur,
  onChange,
  disabled,
  dataError,
  helperText,
}: LocationInputProps) => {
  if (dataLoading) {
    return <CircularProgress size="32px" />;
  }

  if (dataError || !options) {
    return <Typography color="error">Unable to load locations</Typography>;
  }

  const locationOptions = options.map(({ id: val, name: label, address }) => ({
    value: val,
    label,
    address: getAddressAsString(address),
  }));

  return (
    <AutocompleteWrapper>
      <Autocomplete
        disableClearable
        options={locationOptions}
        getOptionLabel={(option) => option.label}
        value={locationOptions.find(({ value: key }) => key === value)}
        style={{ width: '100%' }}
        renderOption={(
          {
            'aria-disabled': ariaDisabled,
            'aria-selected': ariaSelected,
            className,
            onClick,
            onMouseMove,
            onTouchStart,
            role,
            tabIndex,
          },
          option,
          state,
        ) => (
          <AutocompleteOption
            aria-disabled={ariaDisabled}
            aria-selected={ariaSelected}
            className={className}
            index={state.index}
            key={uid(option)}
            onClick={onClick}
            onMouseMove={onMouseMove}
            onTouchStart={onTouchStart}
            role={role}
            tabIndex={tabIndex}
          >
            <div>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="body2" color="textSecondary">
                {option.address}
              </Typography>
            </div>
          </AutocompleteOption>
        )}
        disabled={disabled}
        renderInput={renderAutocompleteInput(disabled, !!dataError, helperText)}
        onBlur={onBlur}
        onChange={(_, location: AutocompleteContents<string>) => {
          // Note: this also blocks the user from manually clearing the field
          if (location?.value) {
            onChange(name)(location.value);
          }
        }}
      />
    </AutocompleteWrapper>
  );
};
